import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import MarketLogo from '../../Abstracts/Components/MarketLogo';
import {
	SurveyQuestion,
	SurveyOption,
	SurveyInstanceQuestion,
	SurveyInstance,
	SurveyResponse,
} from '../../Types/DBTypes';

export type FullSurveyQuestion = Omit<SurveyQuestion, 'QuestionType'> &
	(
		| { QuestionType: 'multiple_choice'; Options: SurveyOption[] }
		| { QuestionType: 'short_text' | 'long_text'; Options: null }
	);

export type FullSurveyInstanceQuestion = SurveyInstanceQuestion & {
	Question: FullSurveyQuestion;
};
export type FullSurveyInstance = SurveyInstance & {
	QuestionInstances: FullSurveyInstanceQuestion[];
};

export type SurveyResponseForCreate = Omit<SurveyResponse, 'Id' | 'Created'>;

type FormValues = {
	[key: string]: string;
};

interface SurveyFormProps {
	surveyInstance: FullSurveyInstance;
	onSubmit?: (responses: SurveyResponseForCreate[]) => Promise<void>;
}

export default function SurveyForm(props: SurveyFormProps) {
	const { surveyInstance } = props;
	return (
		<div className="survey-form">
			<MarketLogo id={surveyInstance.MarketId} />
			<Content {...props} />
		</div>
	);
}

function Content(props: SurveyFormProps) {
	const { surveyInstance, onSubmit } = props;
	const { register, handleSubmit } = useForm<FormValues>();

	const [surveySubmitted, setSurveySubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const questions = [...surveyInstance.QuestionInstances].sort((a, b) => a.Question.Position - b.Question.Position);

	const handleOnSubmit = async (data: FormValues) => {
		const responses: SurveyResponseForCreate[] = questions.map((qInstance) => {
			const key = `question_${qInstance.Id}`;
			const value = data[key];

			const isMultipleChoice = qInstance.Question.QuestionType === 'multiple_choice';

			return {
				SurveyInstanceId: surveyInstance.Id,
				InstanceQuestionId: qInstance.Id,
				ResponseText: isMultipleChoice ? null : value || null,
				SelectedOptionId: isMultipleChoice && value ? parseInt(value, 10) : null,
			};
		});

		if (onSubmit) {
			setSubmitting(true);
			try {
				await onSubmit(responses);
				setSurveySubmitted(true);
			} finally {
				setSubmitting(false);
			}
		}
	};

	if (surveySubmitted) {
		return (
			<div className="form-submitted-container">
				<span className="material-symbols-outlined">task_alt</span>
				<p>Form submitted!</p>
			</div>
		);
	}

	return (
		<>
			<h3>{surveyInstance.Name}</h3>
			{surveyInstance.Description ? (
				<p>
					{surveyInstance.Description.split('\n').map((line, index) => (
						<span key={index}>
							{line}
							<br />
						</span>
					))}
				</p>
			) : null}
			<form onSubmit={handleSubmit(handleOnSubmit)}>
				<fieldset disabled={submitting}>
					{questions.map((qInstance) => {
						const question = qInstance.Question;
						const name = `question_${qInstance.Id}`;

						return (
							<React.Fragment key={qInstance.Id}>
								<label className="question-label">
									{question.QuestionText}

									{question.QuestionType === 'short_text' && (
										<input type="text" {...register(name)} />
									)}

									{question.QuestionType === 'long_text' && <textarea {...register(name)}></textarea>}
								</label>
								{question.QuestionType === 'multiple_choice' && (
									<div>
										{question.Options?.map((option) => {
											return (
												<label key={option.Id}>
													<input
														type="radio"
														value={option.Id.toString()}
														{...register(name)}
													/>
													{option.OptionText}
												</label>
											);
										})}
									</div>
								)}
							</React.Fragment>
						);
					})}
				</fieldset>
				<footer>
					<button disabled={submitting} type="submit">
						{submitting ? 'Submitting...' : 'Submit'}
					</button>
				</footer>
			</form>
		</>
	);
}
